import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

// A subscription key only works for the one product it was issued for, and
// Azure API Management rejects it against the others with the same 401 as a
// bad key, so the connection test has to target the subscribed product.
const products: Record<string, { label: string; test: () => string }> = {
  airportinfo: {
    label: "AirportInfo",
    test: () => "https://api.swedavia.se/airportinfo/v2/airports",
  },
  flightinfo: {
    label: "FlightInfo",
    test: () =>
      `https://api.swedavia.se/flightinfo/v2/ARN/departures/${new Date().toISOString().slice(0, 10)}`,
  },
  waittimepublic: {
    label: "WaitTime",
    test: () => "https://api.swedavia.se/waittimepublic/v2/airports/ARN",
  },
};

export const swedavia = defineConnector({
  identifier: "swedavia",
  name: "Swedavia",
  tagLine: "Swedish airport flight and wait time data",
  keywords: "swedavia airports sweden flights arlanda landvetter departures",
  logo,
  protocol: "http",
  tags: ["travel"],
  defaultUrlPattern: ({ product }) =>
    domainToRegex(`api.swedavia.se/${product ?? ""}`),
  auth: (app) =>
    headerAuth([
      { name: "Ocp-Apim-Subscription-Key", value: app.secret.apiKey },
    ]),
  steps: [
    {
      title: "Subscribe to a Swedavia API",
      items: [
        {
          kind: "markdown",
          content:
            "Register at [apideveloper.swedavia.se](https://apideveloper.swedavia.se/) and subscribe your application to the API you need. **AirportInfo Free**, **FlightInfo Free**, and **WaitTime Free** are all self-serve. Each product issues its own subscription key and that key works for no other product, so connect one product per connector.",
        },
        {
          kind: "select",
          label: "API product",
          required: true,
          defaultValue: "airportinfo",
          target: { kind: "configuration", field: "product" },
          options: Object.entries(products).map(([value, { label }]) => ({
            value,
            label,
          })),
        },
      ],
    },
    {
      title: "Paste your subscription key",
      items: [
        {
          kind: "input",
          label: "Subscription key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
          helperText:
            "Take the key from the subscription for the product you picked above.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ product }) => products[product]!.test(),
  },
});
